<template>
  <v-container fluid class="pa-6">
    <v-row align="center" class="mb-3">
      <div class="ml-2">
        <h2 class="text-h5 font-weight-bold">
          {{ isNew ? $t('message.new_automation') : sequence.name }}
        </h2>
      </div>
      <v-spacer></v-spacer>
      <v-switch v-model="sequence.isActive" :label="sequence.isActive ? 'Active' : 'Inactive'" color="success"
        hide-details class="mr-6"></v-switch>
      <v-btn outlined color="primary" large :loading="saving" :disabled="!isValid" @click="saveSequence">
        {{ $t('message.save') }}
      </v-btn>
    </v-row>
    <v-card-text class="mt-0 pt-0">
      {{ $t('message.lead_automations_explained') }}
    </v-card-text>

    <v-row v-if="!loading">
      <v-col cols="12" md="4">
        <v-card outlined class="pa-4 mb-4">
          <div class="text-subtitle-1 mb-4 font-weight-bold">{{ $t('message.configuration') }}</div>
          <v-text-field v-model="sequence.name" label="Automation Name" outlined dense class="mb-2"></v-text-field>
          <v-select v-model="sequence.triggerEvent" :items="triggerOptions" label="Trigger Event" :disabled="!isNew"
            outlined dense persistent-hint :hint="!isNew ? 'Trigger cannot be changed after creation' : ''"></v-select>
        </v-card>

        <v-card outlined class="pa-4 mb-4">
          <div class="text-subtitle-1 mb-2 font-weight-bold">
            <v-icon small left color="error">mdi-octagon-outline</v-icon>
            {{ $t('message.stop_conditions') }}
          </div>
          <div class="text-caption mb-3">{{ $t('message.stop_sequence_if') }}:</div>
          <v-checkbox :disabled="true" v-model="sequence.stopConditions.exitOnStatusChange"
            :label="$t('message.they_become_member')" value="member" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-model="sequence.stopConditions.exitOnStatusChange" :label="$t('message.they_marked_inactive')"
            value="inactive" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-model="sequence.stopConditions.exitOnEventMatch" :label="$t('message.they_check_in')"
            value="first_checkin" dense hide-details class="mt-1"></v-checkbox>
          <v-checkbox v-if="isValidStopCondition('buy_membership')" v-model="sequence.stopConditions.exitOnEventMatch"
            :label="$t('message.they_buy_membership')" value="membership_purchased" dense hide-details
            class="mt-1"></v-checkbox>
          <div class="text-caption mb-3">*{{
            $t('message.select_at_least_one') }}</div>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <div class="text-h6 mb-4">{{ $t('message.steps') }}</div>
        <v-timeline dense align-top v-if="sequence.steps && sequence.steps.length > 0">
          <v-timeline-item v-for="(step, index) in sequence.steps" :key="index" color="primary" small>
            <v-card outlined>
              <v-toolbar flat dense>
                <span class="text-caption font-weight-bold">STEP {{ index + 1 }}</span>
                <v-spacer></v-spacer>
                <v-btn icon x-small color="error" @click="removeStep(index)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>

              <v-card-text>
                <v-row dense>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model.number="step.delayDays" type="number" label="Wait Days" outlined dense
                      :min="index === 0 ? 1 : sequence.steps[index - 1].delayDays + 1" @input="validateDelay(index)"
                      :hint="index > 0 ? `Must be > Step ${index} (${sequence.steps[index - 1].delayDays})` : ''"
                      persistent-hint></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field v-model="step.preferredTime" type="time" label="Send At" outlined
                      dense></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-select v-model="step.channel" :items="channelOptions" label="Channel" outlined dense></v-select>
                  </v-col>
                  <v-col cols="12">
                    <v-autocomplete v-model="step.templateId" :items="templates" item-text="name" item-value="_id"
                      label="Email Template" outlined dense placeholder="Select a template..."></v-autocomplete>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-timeline-item>
        </v-timeline>

        <v-btn block outlined color="primary" class="mt-4" @click="addStep">
          <v-icon left>mdi-plus</v-icon> {{ $t('message.add_step') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-skeleton-loader v-else type="article, actions"></v-skeleton-loader>
  </v-container>
</template>

<script>
import Api from "@/services/Api";

export default {
  name: "AutomatedSequenceBuilder",
  data() {
    return {
      managedAcademyId: this.$store.state.managedAcademy,
      sequenceId: this.$route.params.id,
      isNew: this.$route.params.id === "new",
      loading: true,
      saving: false,
      templates: [],
      channelOptions: [
        { text: 'Email', value: 'email' },
        //{ text: 'WhatsApp', value: 'whatsapp' }
      ],
      triggerOptions: [
        { text: this.$t('message.new_inquiry'), value: "inquiry_created" },
        { text: this.$t('message.new_visitor'), value: "visitor_created" },
        { text: this.$t('message.trial_class_booked'), value: "trial_booked" },
        { text: this.$t('message.first_checkin'), value: "first_checkin" },
        { text: this.$t('message.membership_purchased'), value: "membership_purchased" },
        //{ text: this.$t('message.birthday'), value: "birthday_milestone" }
      ],
      sequence: {
        name: "",
        managedAcademy: null,
        triggerEvent: "visitor_created",
        isActive: true,
        steps: [],
        stopConditions: {
          exitOnStatusChange: ['member'],
          exitOnEventMatch: []
        }
      }
    };
  },
  computed: {
    isValid() {
      const hasName = !!this.sequence.name;
      const hasSteps = this.sequence.steps && this.sequence.steps.length > 0;
      const stepsComplete = hasSteps && this.sequence.steps.every(s => s.templateId && s.delayDays >= 1);

      // ADD THIS: Ensure at least one checkbox is checked in either Status or Event
      const hasStopCondition =
        (this.sequence.stopConditions.exitOnStatusChange && this.sequence.stopConditions.exitOnStatusChange.length > 0) ||
        (this.sequence.stopConditions.exitOnEventMatch && this.sequence.stopConditions.exitOnEventMatch.length > 0);

      return hasName && stepsComplete && hasStopCondition;
    }
  },
  async created() {
    this.managedAcademyId = this.$store.state.managedAcademy;
    await this.fetchTemplates();
    if (!this.isNew) {
      await this.loadSequence();
    } else {
      this.sequence.managedAcademy = this.managedAcademyId;
      this.addStep();
      this.loading = false;
    }
  },
  methods: {
    isValidStopCondition(stopCondition) {
      if (stopCondition == 'buy_membership') {
        if (this.sequence.triggerEvent.indexOf('membership_purchased') >= 0) {
          return false;
        }
      }
      return true;
    },
    validateDelay(index) {
      const step = this.sequence.steps[index];

      // Rule for Step 1
      if (index === 0) {
        if (step.delayDays < 1) step.delayDays = 1;
      }
      // Rule for subsequent steps
      else {
        const previousDelay = this.sequence.steps[index - 1].delayDays;
        if (step.delayDays <= previousDelay) {
          step.delayDays = previousDelay + 1;
        }
      }
    },
    async fetchTemplates() {
      try {
        const response = await Api().get(`documents/academy/${this.managedAcademyId}/email/templates?archived=false`);
        if (response.data && response.data.templates) {
          this.templates = response.data.templates;
        }
      } catch (err) {
        console.error("Template fetch failed", err);
      }
    },
    async loadSequence() {
      this.loading = true;
      try {
        const res = await Api().get(`automations/managedacademy/${this.managedAcademyId}/sequence/${this.sequenceId}`);
        if (res.data) {
          const data = res.data;
          if (!data.steps) data.steps = [];
          if (!data.stopConditions) data.stopConditions = { exitOnStatusChange: [], exitOnEventMatch: [] };
          this.sequence = data;
        }
      } catch (err) {
        console.error("Load error", err);
      } finally {
        this.loading = false;
      }
    },
    addStep() {
      const lastStep = this.sequence.steps[this.sequence.steps.length - 1];
      const nextDelay = lastStep ? lastStep.delayDays + 1 : 1;

      this.sequence.steps.push({
        stepOrder: this.sequence.steps.length + 1,
        delayDays: nextDelay,
        preferredTime: "07:00",
        channel: "email",
        templateId: null,
        conditions: { requireCheckin: false, requireNoCheckin: false }
      });
    },
    removeStep(index) {
      this.sequence.steps.splice(index, 1);
      this.sequence.steps.forEach((s, i) => s.stepOrder = i + 1);
    },
    async saveSequence() {
      this.saving = true;
      try {
        this.sequence.managedAcademy = this.managedAcademyId;
        await Api().post(`automations/managedacademy/${this.managedAcademyId}`, this.sequence);
        this.goBack();
      } catch (err) {
        console.error("Save error", err);
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>